import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import clsx from 'clsx';
import { useAssetCdn } from '@/context/Ui';
import { SymbolType, CdnObjectType } from '@/types';
import { AudioPlayer } from '@/components';
import { isBoldPhoneme, findSoundsInString } from '@/utils/sounds';

interface SpellProps {
  input: string;
  sounds: Array<string>;
  muteAudio?: boolean;
  onEnded?: () => void;
  applySentenceCase?: boolean;
}

export interface Spell {
  play: () => void;
}

const BETWEEN_LETTERS_PAUSE = 100;
const AFTER_LETTERS_PAUSE = 1000;
const AFTER_WORD_PAUSE = 500;

export const Spell = forwardRef<Spell, SpellProps>(
  (
    { input, sounds, muteAudio = false, onEnded, applySentenceCase = false },
    ref,
  ) => {
    const audioPlayerRef = useRef<AudioPlayer>(null);

    useImperativeHandle(ref, () => ({
      play: () => {
        setCurrentWordIndex(0);
        setCurrentLetterIndex(-1);
        //setCurrentAction('sayingCurrentLetters');
        if (['a', 'A'].includes(words[0])) {
          setCurrentAction('sayingCurrentWord');
        } else {
          setCurrentAction('sayingCurrentLetters');
        }
      },
    }));

    const [currentAction, setCurrentAction] = useState<
      'paused' | 'sayingCurrentLetters' | 'sayingCurrentWord' | 'sayingSentence'
    >('paused');

    const [audio, setAudio] = useState<string | Array<string> | undefined>();

    const words = useMemo<Array<string>>(() => input.split(' '), [input]);

    const hasFullStop = useMemo<boolean>(() => input.endsWith('.'), [input]);

    const hasQuestionMark = useMemo<boolean>(
      () => input.endsWith('?'),
      [input],
    );

    const parts = useMemo<Array<Array<string>>>(
      () => input.split(' ').map((word) => findSoundsInString(sounds, word)),
      [input, sounds],
    );

    const isSingleWord = useMemo<boolean>(() => parts.length === 1, [parts]);

    const [currentWordIndex, setCurrentWordIndex] = useState(-1);

    const [currentLetterIndex, setCurrentLetterIndex] = useState(-1);

    const sentenceAudio = useAssetCdn(
      input,
      SymbolType.Sentence,
      CdnObjectType.Audio,
    );

    const wordAudio = useAssetCdn(
      words?.[currentWordIndex] || '',
      SymbolType.Word,
      CdnObjectType.Audio,
    );

    const soundsAudio = useAssetCdn(
      parts?.[currentWordIndex] || '',
      SymbolType.Sound,
      CdnObjectType.Audio,
      false,
      { word: words?.[currentWordIndex] },
    );

    const loadLetters = useCallback(() => {
      setAudio(soundsAudio);
    }, [parts, currentWordIndex]);

    const loadWords = useCallback(() => {
      setAudio(wordAudio);
    }, [words, currentWordIndex]);

    const loadSentence = useCallback(() => {
      setAudio(sentenceAudio);
    }, [input]);

    const letterIsCurrent = useCallback(
      (wordIndex: number, letterIndex: number) =>
        wordIndex === currentWordIndex && letterIndex === currentLetterIndex,
      [currentWordIndex, currentLetterIndex],
    );

    const wordIsBeingSpoken = useCallback(
      (wordIndex: number) =>
        currentAction === 'sayingCurrentWord' && wordIndex === currentWordIndex,
      [currentAction, currentWordIndex],
    );

    const tryFullStop = useCallback(
      (wordIndex: number, letterIndex: number) =>
        parts.length === 1 ||
        wordIndex < parts.length - 1 ||
        letterIndex < parts[wordIndex].length - 1 ? (
          ''
        ) : hasQuestionMark ? (
          <span className="font-normal">?</span>
        ) : hasFullStop ? (
          <span className="font-normal">.</span>
        ) : null,
      [parts, hasQuestionMark, hasFullStop],
    );

    const getSoundFromInput = useCallback(
      (wordIndex: number, soundIndex: number) => {
        let currentPosition = 0;

        for (let i = 0; i < wordIndex; i++) {
          currentPosition += parts[i].join('').length + 1;
        }

        for (let j = 0; j < soundIndex; j++) {
          currentPosition += parts[wordIndex][j].length;
        }

        const soundLength = parts[wordIndex][soundIndex].length;

        return input.substring(currentPosition, currentPosition + soundLength);
      },
      [input, parts],
    );

    useEffect(() => {
      switch (currentAction) {
        case 'sayingCurrentLetters': {
          loadLetters();

          break;
        }

        case 'sayingCurrentWord': {
          loadWords();

          break;
        }

        case 'sayingSentence': {
          loadSentence();

          break;
        }
      }
    }, [currentAction, loadLetters, loadWords, loadSentence]);

    useEffect(() => {
      const play = async () => {
        try {
          if (audio?.length && currentAction !== 'paused') {
            await audioPlayerRef.current?.play();
          }
        } catch {}
      };

      play();
    }, [currentAction, audio]);

    return (
      <>
        <div
          className={clsx(
            'relative inline-flex flex-wrap gap-1 justify-center overflow-y-visible',
          )}
        >
          {parts.map((word, wIndex) => (
            <div
              key={`word-${wIndex}`}
              className={clsx(
                'relative inline-flex my-3 px-3 font-sassoonInfant',
                wordIsBeingSpoken(wIndex) &&
                  !['a', 'A'].includes(words[wIndex]) &&
                  `
                    before:absolute before:-bottom-4 before:left-0 before:bg-black before:h-2 before:rounded before:animate-sweep-line
                    after:absolute after:top-full after:right-0 after:bg-red-700 after:w-6 after:h-6 after:rounded-full after:opacity-0 after:animate-dot
                  `,
              )}
            >
              {word.map((sound, sIndex) => (
                <div
                  key={`letter-${sIndex}`}
                  className={clsx(
                    'relative text-6xl p-1',
                    isBoldPhoneme(
                      getSoundFromInput(wIndex, sIndex),
                      words[wIndex],
                    ) && 'font-bold',
                    letterIsCurrent(wIndex, sIndex) &&
                      `
                        after:absolute after:top-full after:translate-y-3 after:left-1/2 after:-translate-x-1/2
                        after:w-3 after:h-3 after:bg-black after:rounded-full
                      `,
                    applySentenceCase &&
                      wIndex === 0 &&
                      sIndex === 0 &&
                      'first-letter:uppercase',
                  )}
                >
                  {getSoundFromInput(wIndex, sIndex)}
                  {tryFullStop(wIndex, sIndex)}
                </div>
              ))}
            </div>
          ))}
        </div>

        {audio && (
          <AudioPlayer
            ref={audioPlayerRef}
            src={audio}
            pause={BETWEEN_LETTERS_PAUSE}
            muted={muteAudio}
            onPlaySrc={(src, index) => {
              if (currentAction === 'sayingCurrentLetters') {
                setCurrentLetterIndex(index);
              }
            }}
            onEnded={() => {
              switch (currentAction) {
                case 'sayingCurrentLetters': {
                  setTimeout(() => {
                    setCurrentLetterIndex(-1);
                    setCurrentAction('sayingCurrentWord');
                  }, AFTER_LETTERS_PAUSE);

                  break;
                }

                case 'sayingCurrentWord': {
                  if (isSingleWord) {
                    setTimeout(() => {
                      setCurrentWordIndex(-1);
                      setCurrentAction('paused');
                    }, AFTER_WORD_PAUSE);

                    onEnded?.();
                  } else if (currentWordIndex === words.length - 1) {
                    setTimeout(() => {
                      setCurrentWordIndex(-1);
                      setCurrentAction('sayingSentence');
                    }, AFTER_WORD_PAUSE);
                  } else {
                    setTimeout(() => {
                      setCurrentWordIndex(currentWordIndex + 1);

                      if (['a', 'A'].includes(words[currentWordIndex + 1])) {
                        setCurrentAction('sayingCurrentWord');
                      } else {
                        setCurrentAction('sayingCurrentLetters');
                      }
                    }, AFTER_WORD_PAUSE);
                  }

                  break;
                }

                case 'sayingSentence': {
                  setCurrentAction('paused');

                  onEnded?.();

                  break;
                }
              }
            }}
          />
        )}
      </>
    );
  },
);

Spell.displayName = 'Spell';
